import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { fetchData } from "../utils/api.js";
import { CheckIcon, QuestionIcon, XIcon } from "./common/Icons.js";
import "./GameAttendance.css";

const GameAttendance = ({ gameId, user, isPlayer, isCoach }) => {
  const [attendance, setAttendance] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const currentUserId = user?.firebaseUid || user?.id;

  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        const data = await fetchData(
          `/game_attendance_details?game_id=eq.${gameId}`,
        );
        if (data) {
          const attendanceMap = {};
          data.forEach((r) => (attendanceMap[r.firebase_id] = r.status));
          setAttendance(attendanceMap);
        }
      } catch (e) {
        console.error("Failed to fetch attendance:", e);
      } finally {
        setIsLoading(false);
      }
    };
    if (user && (isPlayer || isCoach)) {
      void fetchAttendance();
    } else {
      setIsLoading(false);
    }
  }, [gameId, user, isPlayer, isCoach]);

  const handleUpdate = async (status) => {
    if (!currentUserId) return;
    setAttendance((prev) => ({ ...prev, [currentUserId]: status }));
    try {
      await fetchData(`/rpc/upsert_attendance`, {
        method: "POST",
        body: JSON.stringify({
          p_game_id: gameId,
          p_firebase_id: currentUserId,
          p_status: status,
        }),
      });
    } catch (e) {
      console.error("Failed to update attendance:", e);
    }
  };

  if (isLoading || (!isPlayer && !isCoach)) return null;

  const currentStatus =
    (currentUserId && attendance[currentUserId]) || "unknown";

  const tally = { yes: 0, maybe: 0, no: 0 };
  Object.values(attendance).forEach((val) => {
    if (tally[val] !== undefined) tally[val]++;
  });

  return (
    <div className="game-attendance-container">
      {isPlayer && (
        <fieldset className="game-rsvp-circles" aria-label="RSVP">
          <button
            type="button"
            className={`rsvp-circle-btn yes ${currentStatus === "yes" ? "active" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              void handleUpdate("yes");
            }}
            aria-label="Attending"
            title="Attending"
          >
            <CheckIcon size="xs" />
          </button>
          <button
            type="button"
            className={`rsvp-circle-btn maybe ${currentStatus === "maybe" ? "active" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              void handleUpdate("maybe");
            }}
            aria-label="Maybe"
            title="Maybe"
          >
            <QuestionIcon size="xs" />
          </button>
          <button
            type="button"
            className={`rsvp-circle-btn no ${currentStatus === "no" ? "active" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              void handleUpdate("no");
            }}
            aria-label="Not Attending"
            title="Not Attending"
          >
            <XIcon size="xs" />
          </button>
        </fieldset>
      )}

      {isCoach && !isPlayer && (
        <div className="game-attendance-tally">
          <div className="tally-item yes" title="Attending">
            <CheckIcon size="xs" /> <span>{tally.yes}</span>
          </div>
          <div className="tally-item maybe" title="Maybe">
            <QuestionIcon size="xs" /> <span>{tally.maybe}</span>
          </div>
          <div className="tally-item no" title="Not Attending">
            <XIcon size="xs" /> <span>{tally.no}</span>
          </div>
        </div>
      )}
    </div>
  );
};

GameAttendance.propTypes = {
  gameId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  user: PropTypes.object.isRequired,
  isPlayer: PropTypes.bool,
  isCoach: PropTypes.bool,
};

export default GameAttendance;
